import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, ListGroup, Image, Card } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import Message from '../components/Message';
import CheckoutSteps from '../components/CheckoutSteps';
import Loader from '../components/Loader';
import { clearCartItems } from '../slices/cartSlice';
import { useCreateOrderMutation } from '../slices/ordersApiSlice';
import { usePayPalCheckout } from '../hooks/usePayPalCheckout';
import { toast } from 'react-toastify';

const PlaceOrderScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);

  const [createdOrder, setCreatedOrder] = useState(null);

  const [createOrder, { isLoading: creatingOrder, error: orderError }] =
    useCreateOrderMutation();

  // Redirect if missing checkout info
  useEffect(() => {
    if (!cart.shippingAddress?.address) navigate('/shipping');
    else if (!cart.paymentMethod) navigate('/payment');
  }, [cart.shippingAddress, cart.paymentMethod, navigate]);

  // SAFELY SANITIZE CART ITEMS (CRASH PROOF)
  const safeCartItems = (cart.cartItems || []).filter(Boolean).map((item) => ({
    _id: item?._id || '',
    name: item?.name || 'Unnamed Product',
    image: item?.image || '/images/placeholder.png',
    price: Number(item?.price || 0),
    qty: Number(item?.qty || 1),
    product: item?.product || item?._id || '',
    variant: item.variant || {},
  }));

  // PayPal hook (only after order exists)
  const { PayPalButtonWrapper, isPending: paypalPending } = usePayPalCheckout(
    createdOrder?._id,
    cart.totalPrice,
    () => {
      toast.success('Payment successful!');
      dispatch(clearCartItems());
      navigate(`/order/${createdOrder._id}`);
    }
  );

  // CREATE ORDER
  const placeOrderHandler = async () => {
    if (!safeCartItems.length) return;

    try {
      const res = await createOrder({
        orderItems: safeCartItems,
        shippingAddress: cart.shippingAddress,
        paymentMethod: cart.paymentMethod,
        itemsPrice: cart.itemsPrice,
        shippingPrice: cart.shippingPrice,
        taxPrice: cart.taxPrice,
        totalPrice: cart.totalPrice,

      }).unwrap();

      setCreatedOrder(res);
      toast.success('Order created successfully!');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  return (
    <>
      <CheckoutSteps step1 step2 step3 step4 />

      <Row>
        {/* LEFT SIDE */}
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Shipping</h2>
              <p>
                {cart.shippingAddress?.address}, {cart.shippingAddress?.city}{' '}
                {cart.shippingAddress?.postalCode}, {cart.shippingAddress?.country}
              </p>
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Payment Method</h2>
              <strong>{cart.paymentMethod}</strong>
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Order Items</h2>

              {safeCartItems.length === 0 ? (
                <Message>Your cart is empty</Message>
              ) : (
                <ListGroup variant="flush">
                  {safeCartItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row>
                        <Col md={2}>
                          <Image src={item.image} alt={item.name} fluid rounded />
                        </Col>

                        <Col>
                          <Link to={`/product/${item.product}`}>
                            {item.name}
                          </Link>
                        </Col>

                        <Col md={4}>
                          {item.qty} x ${item.price.toFixed(2)} = $
                          {(item.qty * item.price).toFixed(2)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>

        {/* RIGHT SIDE */}
        <Col md={4}>
          <Card>
            <ListGroup variant="flush">

              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>

              <ListGroup.Item>
                Items: ${Number(cart.itemsPrice || 0).toFixed(2)}
              </ListGroup.Item>

              <ListGroup.Item>
                Shipping: ${Number(cart.shippingPrice || 0).toFixed(2)}
              </ListGroup.Item>

              <ListGroup.Item>
                Tax: ${Number(cart.taxPrice || 0).toFixed(2)}
              </ListGroup.Item>

              <ListGroup.Item>
                Total: ${Number(cart.totalPrice || 0).toFixed(2)}
              </ListGroup.Item>

              {orderError && (
                <ListGroup.Item>
                  <Message variant="danger">
                    {orderError.data?.message || orderError.message}
                  </Message>
                </ListGroup.Item>
              )}

              <ListGroup.Item>
                {creatingOrder && <Loader />}

                {!createdOrder && (
                  <button
                    className="btn btn-primary w-100"
                    disabled={!safeCartItems.length || creatingOrder}
                    onClick={placeOrderHandler}
                  >
                    Place Order
                  </button>
                )}

                {createdOrder && (
                  <>
                    <Message variant="info">
                      Order created. Complete payment below.
                    </Message>

                    <PayPalButtonWrapper />
                  </>
                )}

                {paypalPending && <Loader />}
              </ListGroup.Item>

            </ListGroup>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PlaceOrderScreen;
